import { Grid, Typography } from "@mui/material";
import { Box } from "@mui/system";
import React from "react";
import { useTranslation } from "react-i18next";
import { useEffect } from "react";
import { useState } from "react";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Button, Input } from "../../controls";
import * as actions from "../../api/actions/pos";
import { Model } from "../../hooks";
import { controller, endpoints } from "../../api/actions/api";
import { NestedFormData, RmvEmptyProp } from "../../hooks/Method";
import { safeOnComplete } from "../../utils/safeOnComplete";
import { formatCurrency } from "../../hooks/Currency";

import {
  printReceiptHtmlDirect,
  buildReceiptHtml,
  getPrintAgentStatus,
} from "../../api/actions/printAgent";



const Checkout = (props) => {
  const { t } = useTranslation();
  const {
    subTotal,
    cartItems,
    totalPay,
    tax,
    selectedCustomer,
    setReceiptData,
    handleClearCart,
    _post,
    _get,
    _authUser,
    _store,
    _getById,
    //_setVal,
    setAlert,
  } = props;
  const { SellMdl } = Model();
  const navigate = useNavigate();
  const [sellData, setSellData] = useState(SellMdl());
  const [loading, setLoading] = useState(false);
  const [payment, setPayment] = useState({
    method: 1,        // 1 cash, 2 card, 3 split, 4 pay later
    cash: "",
    card: "",
    stripe: 0,
  });

  const [availableCredit, setAvailableCredit] = useState(0);
  const [creditUsed, setCreditUsed] = useState(0);

  const cashAmount = Number(payment.cash || 0);
  const cardAmount = Number(payment.card || 0);
  const totalPayNum = Number(totalPay || 0);

  const totalPaid = cashAmount + cardAmount;

  // Amount that still needs to be paid with cash/card
  // after customer credit is applied.
  const amountAfterCredit = Math.max(
    totalPayNum - creditUsed,
    0
  );

  const change =
    payment.method === 1
      ? Math.max(cashAmount - amountAfterCredit, 0)
      : 0;

  const remaining = Math.max(
    totalPayNum - creditUsed - totalPaid,
    0
  );
     //reusable refresh function for after add/edit product and inventory or checkout in POS
  const refreshInventory = React.useCallback(() => {
    if (!_store?.id) return;

    // Refresh Inventory data
    _getById(
      endpoints.Inventory + controller.GetProdsByStore,
      _store.id,
      actions.ActionType.SetInventory
    );

    // Refresh POS Products so "In Stock" updates immediately
    _getById(
      endpoints.Product + controller.GetByStore,
      _store.id,
      actions.ActionType.SetProducts
    );
  }, [_store?.id, _getById]);

  const clear = () => {
    setPayment((p) => {
      if (p.method === 2) {
        return { ...p, card: "" };
      }

      return { ...p, cash: "" };
    });
  };

  const updateCash = (value) => {
    setPayment((p) => {
      if (p.method === 3) {
        let cash = Number(value || 0);

        // In Split mode, cash cannot be greater than the sale total
        cash = Math.min(Math.max(cash, 0), amountAfterCredit);

        return {
          ...p,
          cash: cash === 0 && value !== "" ? "0" : value === "" ? "" : String(cash),
          card: Math.max(amountAfterCredit - cash, 0).toFixed(2),
        };
      }

      return {
        ...p,
        cash: value,
      };
    });
  };

  const updateCard = (value) => {
    setPayment((p) => ({ ...p, card: value }));
  };
 
  const handleNum = (value) => {
    if (value === null || value === undefined) return;

    const v = String(value);

    if (payment.method === 2) {
      updateCard((payment.card || "") + v);
      return;
    }

    const newCash = (payment.cash || "") + v;
    updateCash(newCash);
  };

  const handleAddCash = (amount) => {
    const newCash =
      (parseFloat(payment.cash || 0) || 0) + amount;

    updateCash(newCash);
  };

 const handleUseCredit = () => {
    if (!selectedCustomer?.id) return;

    const amount = Math.min(
      Number(availableCredit || 0),
      totalPayNum
    );

    const newAmountDue = Math.max(
      totalPayNum - amount,
      0
    );

    setCreditUsed(amount);

    setPayment((p) => {
      // Card
      if (p.method === 2) {
        return {
          ...p,
          cash: "",
          card: newAmountDue.toFixed(2),
        };
      }

      // Split
      if (p.method === 3) {
        const cash = Math.min(
          Math.max(Number(p.cash || 0), 0),
          newAmountDue
        );

        return {
          ...p,
          cash: cash > 0 ? String(cash) : "",
          card: Math.max(
            newAmountDue - cash,
            0
          ).toFixed(2),
        };
      }

      // Cash / Pay Later
      return {
        ...p,
        cash: "",
        card: "",
      };
    });
  };

  const handleRemoveCredit = () => {
    setCreditUsed(0);

    setPayment((p) => {
      // Card
      if (p.method === 2) {
        return {
          ...p,
          cash: "",
          card: totalPayNum.toFixed(2),
        };
      }

      // Split
      if (p.method === 3) {
        const cash = Math.min(
          Math.max(Number(p.cash || 0), 0),
          totalPayNum
        );

        return {
          ...p,
          cash: cash > 0 ? String(cash) : "",
          card: Math.max(
            totalPayNum - cash,
            0
          ).toFixed(2),
        };
      }

      return {
        ...p,
        cash: "",
        card: "",
      };
    });
  };

  const handleSubmit = () => {
      if (sellData.sellItems.length <= 0) {
        return setAlert({
          isOpen: true,
          type: "warning",
          title: t("cartEmpty"),
        });
      }

      // validation

      // Pay Later is allowed only for a named customer
      if (payment.method === 4 && !selectedCustomer?.id) {
        return setAlert({
          isOpen: true,
          type: "warning",
          title: t("customerRequired"),
          subTitle: t("payLaterNamedCustomerOnly"),
        });
      }

      // Walk-in customers must pay the full amount
      if (!selectedCustomer?.id && totalPaid < totalPayNum) {
        return setAlert({
          isOpen: true,
          type: "warning",
          title: t("paymentIncomplete"),
          subTitle: t("walkInFullPaymentRequired"),
        });
      }

      // Named customer: partial cash payment is allowed
      if (payment.method === 1 && cashAmount <= 0  && remaining > 0) {
        return setAlert({
          isOpen: true,
          type: "warning",
          title: t("cashAmountRequired"),
        });
      }

      // Named customer: partial card payment is allowed
      if (payment.method === 2 && cardAmount <= 0  && remaining > 0) {
        return setAlert({
          isOpen: true,
          type: "warning",
          title: t("cardAmountRequired"),
        });
      }

      // Named customer: partial split payment is allowed
      if (payment.method === 3 && totalPaid <= 0  && remaining > 0) {
        return setAlert({
          isOpen: true,
          type: "warning",
          title: t("paymentAmountRequired"),
        });
      }

      setLoading(true);

      const onComplete = safeOnComplete(
        (res) => {
          if (res.status !== 200) {
            setLoading(false);
            return   setAlert({
                 isOpen: true,
                 type: "error",
                 title: t("inventoryError"),
                 subTitle: res.msg,
               });
             
            }
        //Make Pay Now check the POS printer

            setReceiptData(res.result);

            // Printer Type 1 = POS Receipt
            const printerType = 1;

            const onPrinterComplete = safeOnComplete(
              async (printerRes) => {
                if (
                  printerRes.status !== 200 ||
                  !printerRes.result
                ) {
                  setLoading(false);

                  refreshInventory();
                  handleClearCart();

                  return setAlert({
                    isOpen: true,
                    type: "warning",
                    title: t("printer"),
                    subTitle: t("noDefaultReceiptPrinter2"),
                  });
                }
                
                // Direct print after Pay Now
                const printer = printerRes.result;

                setLoading(false);

                // If AutoPrint is OFF, complete the sale
                // without printing.
                if (!printer.autoPrint) {
                  refreshInventory();
                  handleClearCart();

                  setAlert({
                    isOpen: true,
                    type: "success",
                    title: "Sale Completed",
                    subTitle:
                      "Sale completed successfully. Automatic receipt printing is disabled.",
                  });
                  
                  return;
                }
                
                // Print Agent Status check
                const agentStatus =
                  await getPrintAgentStatus();

                if (!agentStatus.running) {
                  setLoading(false);

                  refreshInventory();
                  handleClearCart();

                  setAlert({
                    isOpen: true,
                    type: "warning",
                    title: t("saleCompleted"),
                    subTitle: t("saleCompletedAutoPrintDisabled"),
                  });

                  return;
                }

                // Wait for React to render the new receipt.
                setTimeout(() => {
                  const receiptElement =
                    document.getElementById("printSellReceipt");

                  if (!receiptElement) {
                    console.error(
                      "PAY NOW PRINT ERROR: printSellReceipt was not found."
                    );

                    refreshInventory();
                    handleClearCart();
                    return;
                  }

                  const receiptHtml = buildReceiptHtml(
                    receiptElement,
                    printer.paperWidth || 80
                  );

                  printReceiptHtmlDirect(
                    printer.printerName,
                    receiptHtml,
                    printer.paperWidth || 80
                  )
                    .then((result) => {
                      console.log(
                        "PAY NOW DIRECT PRINT:",
                        result
                      );
                    })

                    .catch((err) => {
                      console.error(
                        "PAY NOW DIRECT PRINT ERROR:",
                        err
                      );

                      setAlert({
                        isOpen: true,
                        type: "warning",
                        title: t("saleCompleted"),
                        subTitle: t("printAgentNotRunning2"),
                      });
                    })
                    .finally(() => {
                      refreshInventory();
                      handleClearCart();
                    });

                }, 500);
                // end Direct print after Pay Now

              },
              (err) => {
                setLoading(false);

                setAlert({
                  isOpen: true,
                  type: "warning",
                  title: t("printer"),
                  subTitle:
                    err?.msg ||  t("receiptPrinterNotFound"),
                });

                // Sale already succeeded, so still refresh inventory/cart.
                refreshInventory();
                handleClearCart();
              }
            );

            _get(
              endpoints.StorePrinter +
                controller.GetDefaultPrinter +
                `/${_store.id}/${printerType}`,
              null,
              onPrinterComplete
            );

        },
        (err) => {
          setLoading(false);
          setAlert({
            isOpen: true,
            type: "error",
            title: t("paymentError"),
            subTitle: err?.message || err?.msg,
          });
        }
      );

      const payload = {
        ...sellData,

        sellItems: cartItems.map((j) =>
          j.itemType === 2
            ? { ...j, productId: "" }
            : j
        ),

        cashAmount,
        cardAmount,
        change,

        // Actual new money received
        received: totalPaid,

        // Existing customer credit applied to this sale
        customerCreditUsed: creditUsed,
      };

        const formData = NestedFormData(RmvEmptyProp(payload));

        if (payload.customerId == null) {
          formData.delete("customerId");
        }

        _post(
          endpoints.Sell,
          formData,
          null,
          onComplete
        );
    };

    useEffect(() => {
        setCreditUsed(0);
      // Walk-in customer has no account credit
      if (!selectedCustomer?.id) {
        setAvailableCredit(0);
        return;
      }

    const onComplete = safeOnComplete(
      (res) => {
        if (res.status === 200) {
          setAvailableCredit(
            Number(res.result?.credit || 0)
          );
        } else {
          setAvailableCredit(0);
        }
      },
      () => {
        setAvailableCredit(0);
      }
    );

    _get(
      endpoints.CustomerPayment +
        "/AvailableCredit/" +
        selectedCustomer.id,
      null,
      onComplete
    );
  }, [selectedCustomer?.id, _get]);


    useEffect(() => {
      setSellData((prev) => ({
        ...prev,
        sellItems: cartItems,
        subTotal,
        tax,
        taxPct: _store.tax,
        totalPay,

        cashAmount: Number(payment.cash || 0),
        cardAmount: Number(payment.card || 0),

        received:
          Number(payment.cash || 0) + Number(payment.card || 0),

        userId: _authUser.id,
        storeId: _store.id,

        customerId: selectedCustomer?.id ?? null,
      }));
    }, [cartItems, payment, totalPay, subTotal, tax, _store.tax, _authUser.id,
       _store.id, selectedCustomer,]);

  return (
    <>
      {/* Display */}
      <Grid className="tx-center gray-color mb-10 p-10">
        {payment.method === 1 && (
          <Input
            value={payment.cash}
            onChange={(e) =>updateCash(e.target.value)}
          />
        )}

        {payment.method === 2 && (
          <Input
            value={payment.card}
            onChange={(e) => updateCard(e.target.value)}
          />
        )}

        {payment.method === 3 && (
          <>
            <Input
              placeholder={t("cash")}
              onChange={(e) =>updateCash(e.target.value)}
              value={payment.cash}  
            />
            <Input
              placeholder={t("card")}
              //onChange={(e) =>updateCard(e.target.value)}
              value={payment.card}
              disabled
            />
          </>
        )}
          <Box className="mt-10">
          <Typography>
            {t("paymentType")}:{" "}
            {payment.method === 1
              ? t("cash")
              : payment.method === 2
              ? t("card")
              : payment.method === 3
              ? t("split")
              : t("payLater")}
          </Typography>

          {/* Customer Available Credit */}
          {selectedCustomer?.id && (
            <Typography
              variant="subtitle1"
              className="fs-22 fw-6"
            >
              {t("availableCredit")}:&nbsp;&nbsp;
              {formatCurrency(availableCredit, _store)}
            </Typography>
          )}

          {/* Use / Remove Customer Credit */}
           {selectedCustomer?.id && availableCredit > 0 && (
            <Box className="mt-10 mb-10">
              {creditUsed <= 0 ? (
                <Button
                  onClick={handleUseCredit}
                  className="w-100pr"
                >
                  {t("useCredit")}{" "}
                  {formatCurrency(
                    Math.min(availableCredit, totalPayNum),
                    _store
                  )}
                </Button>
              ) : (
                <>
                  <Typography
                    variant="subtitle1"
                    className="fs-22 fw-6"
                  >
                    {t("creditUsed")}:&nbsp;&nbsp;
                    {formatCurrency(creditUsed, _store)}
                  </Typography>

                  <Button
                    onClick={handleRemoveCredit}
                    className="w-100pr"
                  >
                    {t("removeCredit")}
                  </Button>
                </>
              )}
            </Box>
          )} 
          {/* Sale Total */}
          <Typography variant="subtitle1" className="fs-22 fw-6">
            {t("total")}:&nbsp;&nbsp;
            {formatCurrency(totalPay, _store)}
          </Typography>

          <Typography variant="subtitle1" className="fs-22 fw-6">
            {t("change")}:&nbsp;&nbsp;
            {formatCurrency(change, _store)}
          </Typography>

          <Typography variant="subtitle1" className="fs-22 fw-6">
            {t("paid")}:&nbsp;&nbsp;
            {formatCurrency(totalPaid, _store)}
          </Typography>

          <Typography>
            {t("remaining")}: {formatCurrency(remaining, _store)}
          </Typography>

        </Box>
      </Grid>

      <Box
        className="d-flex"
        sx={{
          height: { xs: "calc(100vh - 400px)", md: "calc(100vh - 440px)" },
          flexWrap: { xs: "wrap", md: "inherit" },
        }}
      >
        <Box className="w-100pr d-grid" sx={{ mr: { md: "10px" } }}>
          <Box className="d-flex mb-10">
            <Button
              size="large"
              onClick={() => handleAddCash(5)}
              className="w-100pr fs-30 mr-10"
            >
              {formatCurrency(5, _store)}
            </Button>
            <Button
              size="large"
               onClick={() => handleAddCash(10)}
              className="w-100pr fs-30 mr-10"
            >
              {formatCurrency(10, _store)}
            </Button>
            <Button
              size="large"
              onClick={() => handleAddCash(20)}
              className="w-100pr fs-30 mr-10"
            >
              {formatCurrency(20, _store)}
            </Button>
            <Button
              size="large"
              onClick={() => handleAddCash(50)}
              className="w-100pr fs-30"
            >
              {formatCurrency(50, _store)}
            </Button>
          </Box>

          <Box className="d-flex mb-10">
            <Button
              size="large"
              onClick={() => handleNum(1)}
              className="w-100pr bg-white gray-color fs-30 mr-10"
            >
              1
            </Button>
            <Button
              size="large"
              onClick={() => handleNum(2)}
              className="w-100pr bg-white gray-color fs-30 mr-10"
            >
              2
            </Button>
            <Button
              size="large"
              onClick={() => handleNum(3)}
              className="w-100pr bg-white gray-color fs-30 mr-10"
            >
              3
            </Button>
            <Button
              size="large"
              onClick={() => handleNum(4)}
              className="w-100pr bg-white gray-color fs-30"
            >
              4
            </Button>
          </Box>

          <Box className="d-flex mb-10">
            <Button
              size="large"
              onClick={() => handleNum(5)}
              className="w-100pr bg-white gray-color fs-30 mr-10"
            >
              5
            </Button>
            <Button
              size="large"
              onClick={() => handleNum(6)}
              className="w-100pr bg-white gray-color fs-30 mr-10"
            >
              6
            </Button>
            <Button
              size="large"
              onClick={() => handleNum(7)}
              className="w-100pr bg-white gray-color fs-30 mr-10"
            >
              7
            </Button>
            <Button
              size="large"
              onClick={() => handleNum(8)}
              className="w-100pr bg-white gray-color fs-30"
            >
              8
            </Button>
          </Box>

          <Box className="d-flex mb-10">
            <Button
              size="large"
              onClick={() => handleNum(9)}
              className="w-100pr bg-white gray-color fs-30 mr-10"
            >
              9
            </Button>
            <Button
              size="large"
              onClick={() => handleNum(0)}
              className="w-100pr bg-white gray-color fs-30 mr-10"
            >
              0
            </Button>
            <Button
              size="large"
              onClick={() => handleNum(".")}
              className="w-100pr bg-white gray-color fs-30 mr-10"
            >
              .
            </Button>
            <Button
              size="large"
              onClick={clear}
              className="w-100pr bg-white gray-color"
              sx={{ fontSize: { xs: "16px", md: "22px" } }}
            >
              {t("clear")}
            </Button>
          </Box>
        </Box>

        <Box
          className="mb-10"
          sx={{
            width: { xs: "100%", md: "220px" },
            display: { xs: "flex", md: "grid" },
          }}
        >
          <Button
            size="large"
             onClick={() => setPayment(p => ({ ...p, method: 1 }))}
            className="w-100pr bg-white gray-color fs-20"
            sx={{ mb: { md: "10px" }, mr: { xs: "10px" } }}
          >
            {t("cash")}
          </Button>
          <Button
            size="large"
            onClick={() =>
                      setPayment(p => ({
                        ...p,
                        method: 2,
                        cash: "",
                        card: amountAfterCredit.toFixed(2),
                      }))
                    }
            className="w-100pr bg-white gray-color fs-20"
             sx={{ mb: { md: "10px" }, mr: { xs: "10px" } }}
          >
            {t("card")}
          </Button>
          
          <Button
            size="large"
            onClick={() =>
              setPayment((p) => {
                const cash = Math.min(
                  Math.max(Number(p.cash || 0), 0),
                  amountAfterCredit
                );

                return {
                  ...p,
                  method: 3,
                  cash: cash > 0 ? cash.toString() : "",
                  card: Math.max(amountAfterCredit - cash, 0).toFixed(2),
                };
              })
            }
            className="w-100pr bg-white gray-color fs-20"
            sx={{ mb: { md: "10px" }, mr: { xs: "10px" } }}
          >
            {t("split")}
          </Button>

          <Button
            size="large"
            onClick={() =>
              setPayment((p) => ({
                ...p,
                method: 4,
                cash: "",
                card: "",
              }))
            }
            className="w-100pr bg-white gray-color fs-20"
            sx={{ mb: { md: "10px" }, mr: { xs: "10px" } }}
          >
            {t("payLater")}
          </Button>
          <Button
            size="large"
            onClick={() => navigate("/pos")}
            className="w-100pr bg-white gray-color fs-20"
            sx={{ mb: { md: "10px" }, mr: { xs: "10px" } }}
          >
            {t("cancel")}
          </Button>
          <Button
            loading={loading}
            size="large"
            onClick={handleSubmit}
            className="w-100pr bg-error white-color fs-26 d-block"
          >
            {t("payNow")}
            {loading && "..."}
          </Button>
        </Box>
      </Box>
    </>
  );
};
const mapStateToProps = (state) => ({
  _type: state.pos.type,
  _store: state.pos.defStore,
  _authUser: state.pos.authUser,
});

const mapActionToProps = {
  _post: actions.post,
  _get: actions.get,
  _getById: actions.getById,
  _setVal: actions.setVal,
};

export default connect(mapStateToProps, mapActionToProps)(Checkout);